import React, { useEffect, useState, useSyncExternalStore } from 'react'
import { Modal, View } from 'react-native'
import { useIsConnected } from 'react-native-offline'
import { Button, Text } from '@components/base'
import { LoadingIndicator } from '@components/LoadingIndicator'
import AppCheckRecovery, { AppCheckRecoveryStatus } from '@config/appCheckRecovery'
import AppCheckRecoveryService, { AppCheckRecoveryResult } from '@config/appCheckRecoveryService'
import { StyleService, useStyleSheet } from '@src/style/service'
import { contactSupport } from '@src/utils/openUrl'
import { redirectToAppPage } from '@src/utils/store'

const INITIAL_MESSAGE =
  'We need to verify this app before connecting to Nutrisense. Try again to continue.'
const RETRY_FAILED_MESSAGE =
  "We still couldn't verify this app. Check for an update, then try again. " +
  "If you're up to date, contact support."
const CONNECTION_FAILED_MESSAGE =
  "We couldn't connect to verify this app. Check your internet connection, then try again."

export const AppCheckRecoveryDialog = () => {
  const styles = useStyleSheet(themedStyles)
  const isConnected = useIsConnected()
  const recoveryStatus = useSyncExternalStore(
    AppCheckRecovery.subscribe,
    AppCheckRecovery.getSnapshot,
    AppCheckRecovery.getSnapshot,
  )
  const [isRetrying, setIsRetrying] = useState(false)
  const [retryFailed, setRetryFailed] = useState(false)

  useEffect(() => {
    if (recoveryStatus !== AppCheckRecoveryStatus.Recovering || isConnected !== true) {
      return
    }

    let active = true

    AppCheckRecoveryService.recover().then((result) => {
      if (!active) {
        return
      }

      switch (result) {
        case AppCheckRecoveryResult.Rejected:
          AppCheckRecovery.requireRecovery()
          break
        case AppCheckRecoveryResult.Recovered:
        case AppCheckRecoveryResult.ServerError:
        case AppCheckRecoveryResult.Unavailable:
          AppCheckRecovery.clearRecovery()
          break
      }
    })

    return () => {
      active = false
    }
  }, [isConnected, recoveryStatus])

  const onRetry = async () => {
    if (isConnected !== true) {
      return
    }

    setIsRetrying(true)
    setRetryFailed(false)

    const result = await AppCheckRecoveryService.recover()

    setIsRetrying(false)
    if (result === AppCheckRecoveryResult.Recovered) {
      AppCheckRecovery.clearRecovery()
    } else if (result === AppCheckRecoveryResult.Rejected) {
      setRetryFailed(true)
    } else if (result === AppCheckRecoveryResult.ServerError) {
      AppCheckRecovery.clearRecovery()
    }
  }

  if (recoveryStatus !== AppCheckRecoveryStatus.Required) {
    return null
  }

  let message = INITIAL_MESSAGE
  if (isConnected === false) {
    message = CONNECTION_FAILED_MESSAGE
  } else if (retryFailed) {
    message = RETRY_FAILED_MESSAGE
  }

  return (
    <Modal
      animationType="fade"
      onRequestClose={() => undefined}
      statusBarTranslucent
      transparent
      visible
    >
      <View style={styles.backdrop}>
        <View accessibilityViewIsModal style={styles.dialog}>
          <Text type="title-2" style={styles.title}>
            Unable to verify app
          </Text>
          <Text type="large" style={styles.description}>
            {message}
          </Text>
          <Button
            accessibilityLabel="Retry app verification"
            accessoryRight={
              isRetrying ? (
                <LoadingIndicator color="white" size="small" viewStyles={styles.loadingIndicator} />
              ) : undefined
            }
            disabled={isRetrying || isConnected !== true}
            isAccessoryInline
            onPress={onRetry}
            size="block"
            type="primary"
          >
            {isRetrying ? 'Retrying...' : 'Retry'}
          </Button>
          <Button
            accessibilityLabel="Check for app update"
            disabled={isRetrying}
            onPress={redirectToAppPage}
            size="block"
            style={styles.secondaryButton}
            type="outline"
          >
            Check for Update
          </Button>
          <Button
            accessibilityLabel="Contact customer support"
            disabled={isRetrying}
            onPress={contactSupport}
            size="block"
            style={styles.supportButton}
            type="transparent"
          >
            Contact Support
          </Button>
        </View>
      </View>
    </Modal>
  )
}

const themedStyles = StyleService.create({
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'theme.overlay',
    flex: 1,
    justifyContent: 'center',
    padding: 24,
  },
  dialog: {
    backgroundColor: 'theme.background.modal',
    borderRadius: 16,
    maxWidth: 420,
    padding: 24,
    width: '100%',
  },
  title: {
    textAlign: 'center',
  },
  description: {
    marginBottom: 24,
    marginTop: 12,
    textAlign: 'center',
  },
  secondaryButton: {
    marginTop: 12,
  },
  supportButton: {
    marginTop: 4,
  },
  loadingIndicator: {
    height: 20,
    marginLeft: 4,
  },
})
